/**
 * Home / PunchScreen -- Main punch screen (90% of app usage).
 * SDD 7.3 -- Orchestrates PunchButton, CategoryPicker, Position/Apparatus pickers,
 * ConfirmDialog, StatusBar, and schedule display.
 *
 * Simplified layout: shift entry + schedule only.
 * Hours totals and recent entries live on the "My Hours" page.
 *
 * Desktop layout: Single-column centered layout.
 * Mobile layout: Single-column stacked layout (3-tap max per NFR-USE-01).
 */

import { useState, useCallback, useEffect } from 'react';
import { useAuth } from '../hooks/useAuth';
import { isOfficerOrAbove } from '../lib/auth';
import { usePunch } from '../hooks/usePunch';
import { useMyUpcomingShifts } from '../hooks/useMyUpcomingShifts';
import type { UpcomingShift } from '../hooks/useMyUpcomingShifts';
import { useMediaQuery } from '../hooks/useMediaQuery';

import type { CategoryId } from '@shared/categories';
import { getSubcategoriesForCategory } from '@shared/categories';
import { getApparatusType } from '@shared/apparatus';
import { getLastCategory, getLastPosition, getLastApparatus } from '../lib/storage';
import { apiFetch } from '../lib/api';

import AppLayout from '../components/layout/AppLayout';
import PunchButton from '../components/PunchButton';
import GeoClockBanner from '../components/GeoClockBanner';
import { useGeoHeartbeat } from '../hooks/useGeoHeartbeat';
import CategoryPicker from '../components/CategoryPicker';
import PositionPicker from '../components/PositionPicker';
import ApparatusPicker from '../components/ApparatusPicker';
import ConfirmDialog from '../components/ConfirmDialog';
import StatusBar from '../components/StatusBar';
import CoverageRequestButton from '../components/CoverageRequestButton';
import ShiftTimePicker, { computeShiftTimes } from '../components/ShiftTimePicker';
import type { ShiftMode } from '../components/ShiftTimePicker';
import MemberSelector from '../components/MemberSelector';
import WelcomeNudge from '../components/WelcomeNudge';
import RetroactiveOverlay from '../components/RetroactiveOverlay';
import { useRetroactiveCheck } from '../hooks/useRetroactiveCheck';
import { useOfficerSubmit } from '../hooks/useOfficerSubmit';
import type { OfficerPunchPayload } from '../hooks/useOfficerSubmit';
import { SHIFT_TYPES } from '@shared/categories';

// -- Helpers --

function getTimeOfDayGreeting(): string {
  const now = new Date();
  const etHour = parseInt(
    now.toLocaleString('en-US', { hour: 'numeric', hour12: false, timeZone: 'America/New_York' }),
    10,
  );
  if (etHour >= 5 && etHour < 12) return 'Good morning';
  if (etHour >= 12 && etHour < 17) return 'Good afternoon';
  return 'Good evening';
}

function formatShiftTimeLabel(start: string, end: string): string {
  const fmt = (iso: string) =>
    new Date(iso).toLocaleString('en-US', {
      weekday: 'short',
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
    });
  return `${fmt(start)} - ${fmt(end)}`;
}

// -- Valid categories for defaults --

const VALID_CATEGORIES: CategoryId[] = ['operational', 'administrative', 'training', 'public_event'];

function isValidCategory(val: string | null): val is CategoryId {
  return val !== null && VALID_CATEGORIES.includes(val as CategoryId);
}

// -- Component --

// Battalion color palette (matches DutyIndicator + printed 2026 Duty Calendar)
const BATTALION_COLORS: Record<number, { bg: string; text: string; border: string }> = {
  1: { bg: 'rgba(0, 188, 212, 0.15)', text: '#4DD0E1', border: 'rgba(0, 188, 212, 0.3)' },
  2: { bg: 'rgba(76, 175, 80, 0.15)', text: '#66BB6A', border: 'rgba(76, 175, 80, 0.3)' },
  3: { bg: 'rgba(229, 57, 53, 0.15)', text: '#EF5350', border: 'rgba(229, 57, 53, 0.3)' },
  4: { bg: 'rgba(245, 124, 0, 0.15)', text: '#FFA726', border: 'rgba(245, 124, 0, 0.3)' },
  5: { bg: 'rgba(63, 81, 181, 0.15)', text: '#7986CB', border: 'rgba(63, 81, 181, 0.3)' },
};

function getBnStyle(bn: number) {
  return BATTALION_COLORS[bn] ?? { bg: 'transparent', text: '#999999', border: 'transparent' };
}

export default function Home() {
  const { user } = useAuth();
  const punch = usePunch();
  const upcoming = useMyUpcomingShifts();
  const isDesktop = useMediaQuery('(min-width: 768px)');
  const geoClock = useGeoHeartbeat(true);
  const retroCheck = useRetroactiveCheck();

  // Auto-clock toast state
  const [autoToast, setAutoToast] = useState<{ type: string; message: string } | null>(null);

  // Show toast when auto-clock fires (from heartbeat or retroactive check)
  useEffect(() => {
    const action = geoClock.lastAutoAction ?? retroCheck.lastAutoAction;
    if (!action) return;
    const isClockIn = action.type === 'auto_clock_in';
    setAutoToast({
      type: action.type,
      message: isClockIn
        ? `Clocked in automatically at ${geoClock.stationName ?? 'the station'}`
        : 'Clocked out automatically',
    });
    geoClock.clearAutoAction();
    retroCheck.clearAutoAction();
    // Auto-dismiss after 5 seconds and reload to reflect new punch state
    const timer = setTimeout(() => {
      setAutoToast(null);
      window.location.reload();
    }, 5000);
    return () => clearTimeout(timer);
  }, [geoClock.lastAutoAction, retroCheck.lastAutoAction]); // eslint-disable-line react-hooks/exhaustive-deps

  // Selection state
  const [category, setCategory] = useState<CategoryId | null>(null);
  const [subcategory, setSubcategory] = useState<string | null>(null);
  const [position, setPosition] = useState<string | null>(null);
  const [apparatus, setApparatus] = useState<string | null>(null);
  const [showConfirm, setShowConfirm] = useState(false);

  // Derived: apparatus type for position filtering
  const apparatusType = apparatus ? (getApparatusType(apparatus) ?? null) : null;

  // Shift time picker state
  const [shiftMode, setShiftMode] = useState<ShiftMode>('evening');
  const [customStart, setCustomStart] = useState('');
  const [customEnd, setCustomEnd] = useState('');
  const [shiftSubmitting, setShiftSubmitting] = useState(false);

  // "For Me" / "For Someone Else" toggle (officers/admins only)
  const isOfficerOrAdmin = isOfficerOrAbove(user);
  const [submitMode, setSubmitMode] = useState<'self' | 'other'>('self');
  const officer = useOfficerSubmit();
  const [selectedMemberId, setSelectedMemberId] = useState<number | null>(null);
  const [officerShiftType, setOfficerShiftType] = useState<string>('scheduled');
  const [officerNotes, setOfficerNotes] = useState('');

  // Load remembered defaults on mount
  useEffect(() => {
    const lastCat = getLastCategory();
    if (isValidCategory(lastCat)) setCategory(lastCat);
    else setCategory('operational');

    const lastPos = getLastPosition();
    if (lastPos) setPosition(lastPos);

    const lastApp = getLastApparatus();
    if (lastApp) setApparatus(lastApp);
  }, []);

  // Reset position when apparatus type changes (positions depend on unit type)
  useEffect(() => {
    setPosition(null);
  }, [apparatusType]);

  const handleCategoryChange = useCallback((cat: CategoryId) => {
    setCategory(cat);
    setSubcategory(null);
  }, []);

  const showOperationalFields = category === 'operational';
  const subcategoryOptions = category ? getSubcategoriesForCategory(category) : null;

  const memberSelected = submitMode === 'self' || selectedMemberId !== null;

  const canClockIn =
    category !== null &&
    memberSelected &&
    (!showOperationalFields || (position !== null && position !== '' && apparatus !== null && apparatus !== ''));

  const shiftTimes = computeShiftTimes(shiftMode, customStart, customEnd);
  const canSubmitShift = canClockIn && shiftTimes !== null;

  // -- Handlers --

  const handleClockInPress = useCallback(() => {
    if (!canSubmitShift) return;
    setShowConfirm(true);
  }, [canSubmitShift]);

  const handleConfirm = useCallback(async () => {
    if (!category) return;

    // "For Someone Else" officer submission
    if (submitMode === 'other' && selectedMemberId) {
      if (!shiftTimes) return;
      setShiftSubmitting(true);
      try {
        const payload: OfficerPunchPayload = {
          member_id: selectedMemberId,
          category,
          clock_in: shiftTimes.start,
          clock_out: shiftTimes.end,
          shift_type: officerShiftType,
        };
        if (subcategory) payload.subcategory = subcategory;
        if (showOperationalFields && position) payload.position = position;
        if (showOperationalFields && apparatus) payload.apparatus = apparatus;
        if (officerNotes.trim()) payload.notes = officerNotes.trim();

        await officer.submitEntry(payload);
        setShowConfirm(false);
        setSelectedMemberId(null);
        setOfficerNotes('');
      } catch {
        // Keep dialog open on error
      } finally {
        setShiftSubmitting(false);
      }
      return;
    }

    if (!shiftTimes) return;
    setShiftSubmitting(true);
    try {
      const payload: Record<string, unknown> = {
        category,
        clock_in: shiftTimes.start,
        clock_out: shiftTimes.end,
        shift_type: 'scheduled',
      };
      if (subcategory) payload.subcategory = subcategory;
      if (showOperationalFields && position) payload.position = position;
      if (showOperationalFields && apparatus) payload.apparatus = apparatus;

      await apiFetch('/api/punch/shift', {
        method: 'POST',
        body: JSON.stringify(payload),
      });
      setShowConfirm(false);
      setShiftMode('evening');
      setCustomStart('');
      setCustomEnd('');
    } catch {
      // Keep dialog open on error
    } finally {
      setShiftSubmitting(false);
    }
  }, [
    category,
    subcategory,
    position,
    apparatus,
    showOperationalFields,
    shiftTimes,
    submitMode,
    selectedMemberId,
    officerShiftType,
    officerNotes,
    officer,
  ]);

  const handleClockOut = useCallback(async () => {
    await punch.clockOut();
  }, [punch]);

  // -- Loading state --

  if (punch.loading) {
    return (
      <AppLayout>
        <div className="min-h-[50dvh] flex items-center justify-center text-content-tertiary text-base">Loading...</div>
      </AppLayout>
    );
  }

  const shiftTimeLabel = shiftTimes ? formatShiftTimeLabel(shiftTimes.start, shiftTimes.end) : null;

  // -- Clocked In View --

  if (punch.active) {
    return (
      <AppLayout>
        <div className={`pt-5 w-full ${isDesktop ? 'max-w-xl mx-auto' : ''}`}>
          <div className="flex flex-col items-center gap-6">
            <StatusBar
              clockIn={punch.active.clock_in}
              elapsedSeconds={punch.elapsedSeconds}
              category={punch.active.category}
              position={punch.active.position}
              apparatus={punch.active.apparatus}
            />

            <PunchButton isClockedIn={true} disabled={punch.submitting} onPress={handleClockOut} />

            {punch.error && <ErrorBanner message={punch.error} onDismiss={punch.clearError} />}
          </div>
        </div>
      </AppLayout>
    );
  }

  // -- Not Clocked In: Punch Controls --

  const punchControls = (
    <>
      {/* Auto-clock toast notification */}
      {autoToast && (
        <div
          className={`w-full px-4 py-3 rounded-lg border text-sm font-semibold text-center ${
            autoToast.type === 'auto_clock_in'
              ? 'bg-green-500/10 border-green-500/30 text-green-400'
              : 'bg-amber-500/10 border-amber-500/30 text-amber-400'
          }`}
        >
          {autoToast.message}
        </div>
      )}

      {/* Geo-clock suggestion banner (suppressed when retroactive overlay is showing) */}
      {geoClock.pendingSuggestion && !retroCheck.retroactive && (
        <GeoClockBanner
          suggestion={geoClock.pendingSuggestion}
          onAccept={async (id) => {
            const success = await geoClock.respondToSuggestion(id, 'accept', {
              category: category ?? 'operational',
              ...(position ? { position } : {}),
              ...(apparatus ? { apparatus } : {}),
            });
            if (success) {
              // Refresh punch status to reflect new clock-in/out
              window.location.reload();
            }
            return success;
          }}
          onDismiss={(id) => geoClock.respondToSuggestion(id, 'dismiss')}
        />
      )}

      {/* Upcoming shifts for this member's battalion */}
      <UpcomingShiftsSection shifts={upcoming.shifts} battalion={upcoming.battalion} loading={upcoming.loading} />

      {/* For Me / For Someone Else toggle — officers/admins only */}
      {isOfficerOrAdmin && (
        <SubmitModeToggle
          mode={submitMode}
          onModeChange={setSubmitMode}
          members={officer.members}
          membersLoading={officer.membersLoading}
          selectedMemberId={selectedMemberId}
          onMemberChange={setSelectedMemberId}
        />
      )}

      {/* Category selection */}
      <div className="w-full">
        <div className="text-xs font-semibold text-content-secondary uppercase tracking-wider mb-2">Category</div>
        <CategoryPicker selected={category} onChange={handleCategoryChange} />
      </div>

      {/* Subcategory -- conditional on category having subcategories (e.g. Administrative) */}
      {subcategoryOptions && (
        <div className="w-full">
          <div className="text-xs font-semibold text-content-secondary uppercase tracking-wider mb-2">Type</div>
          <div className="flex gap-2">
            {subcategoryOptions.map((sub) => {
              const isSelected = subcategory === sub.id;
              return (
                <button
                  key={sub.id}
                  type="button"
                  onClick={() => setSubcategory(sub.id)}
                  aria-pressed={isSelected}
                  className={`
                    flex-1 px-3 py-2.5 rounded-lg border-2 text-xs font-bold
                    transition-all duration-150 cursor-pointer touch-manipulation text-center
                    ${
                      isSelected
                        ? 'border-action-primary bg-action-primary/10 text-action-primary'
                        : 'border-edge-default bg-surface-raised text-content-secondary hover:border-edge-strong hover:text-content-primary'
                    }
                  `}
                >
                  {sub.label}
                </button>
              );
            })}
          </div>
        </div>
      )}

      {/* Apparatus & Position -- only for operational */}
      {showOperationalFields && (
        <div className="w-full flex flex-col gap-4">
          <ApparatusPicker selected={apparatus} onChange={setApparatus} showCommandUnits={isOfficerOrAdmin} />
          <PositionPicker selected={position} onChange={setPosition} apparatusType={apparatusType} />
        </div>
      )}

      {/* Shift Time Picker */}
      <ShiftTimePicker
        mode={shiftMode}
        onModeChange={setShiftMode}
        customStart={customStart}
        customEnd={customEnd}
        onCustomStartChange={setCustomStart}
        onCustomEndChange={setCustomEnd}
      />

      {/* Officer: Shift type & notes for "For Someone Else" */}
      {submitMode === 'other' && (
        <div className="w-full flex flex-col gap-3">
          <div>
            <label
              htmlFor="home-shift-type"
              className="block text-[11px] font-semibold text-content-secondary mb-1 uppercase tracking-wider"
            >
              Shift Type
            </label>
            <select
              id="home-shift-type"
              value={officerShiftType}
              onChange={(e) => setOfficerShiftType(e.target.value)}
              className="w-full px-3 py-2.5 text-sm rounded-lg border-2 border-edge-default bg-surface-raised text-content-primary cursor-pointer appearance-none"
            >
              {SHIFT_TYPES.map((st) => (
                <option key={st.id} value={st.id}>
                  {st.label}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label
              htmlFor="home-notes"
              className="block text-[11px] font-semibold text-content-secondary mb-1 uppercase tracking-wider"
            >
              Notes (optional)
            </label>
            <textarea
              id="home-notes"
              value={officerNotes}
              onChange={(e) => setOfficerNotes(e.target.value)}
              maxLength={500}
              rows={2}
              placeholder="Add notes..."
              className="w-full px-3 py-2.5 text-xs rounded-lg border-2 border-edge-default bg-surface-raised text-content-primary resize-y font-[inherit] box-border"
            />
          </div>
        </div>
      )}

      {/* Submit / Clock-In button */}
      <div className="w-full flex justify-center pt-1 pb-2">
        <button
          type="button"
          onClick={handleClockInPress}
          disabled={!canSubmitShift || (submitMode === 'other' ? officer.submitting : shiftSubmitting)}
          className={`
            px-8 py-2 rounded-full
            text-sm font-semibold tracking-wide
            border-2 border-action-primary text-action-primary bg-action-primary/10
            touch-manipulation select-none
            transition-all duration-150
            ${
              !canSubmitShift || (submitMode === 'other' ? officer.submitting : shiftSubmitting)
                ? 'cursor-not-allowed opacity-40 border-edge-muted text-content-tertiary bg-transparent'
                : 'cursor-pointer hover:bg-action-primary/20 active:scale-[0.97]'
            }
          `}
        >
          Submit Shift
        </button>
      </div>

      {punch.error && <ErrorBanner message={punch.error} onDismiss={punch.clearError} />}
      {officer.error && submitMode === 'other' && (
        <ErrorBanner message={officer.error} onDismiss={officer.clearError} />
      )}
      {officer.success && submitMode === 'other' && (
        <div className="w-full p-3 bg-green-500/15 border border-green-500/30 rounded-xl">
          <div className="text-sm font-semibold text-green-400">Entry submitted successfully</div>
          <button
            type="button"
            onClick={() => {
              officer.clearSuccess();
              setSelectedMemberId(null);
              setOfficerNotes('');
            }}
            className="mt-2 px-3 py-1 text-[12px] font-semibold text-green-400 bg-transparent border border-green-500/30 rounded-md cursor-pointer"
          >
            Submit Another
          </button>
        </div>
      )}
    </>
  );

  // -- Layout --

  return (
    <AppLayout>
      <div className={`w-full pt-4 ${isDesktop ? 'max-w-2xl mx-auto' : ''}`}>
        {/* Greeting + Battalion pill */}
        <div className={`mb-6 flex items-center justify-between ${isDesktop ? '' : 'mb-4'}`}>
          <h1 className={`font-bold text-content-primary ${isDesktop ? 'text-2xl' : 'text-lg font-semibold'}`}>
            {getTimeOfDayGreeting()}, {user?.name?.split(' ')[0] ?? 'there'}.
          </h1>
          {upcoming.battalion && (
            <span
              className="text-[12px] font-bold px-2.5 py-1 rounded-full uppercase tracking-wide whitespace-nowrap"
              style={{
                backgroundColor: getBnStyle(upcoming.battalion.number).bg,
                color: getBnStyle(upcoming.battalion.number).text,
                border: `1px solid ${getBnStyle(upcoming.battalion.number).border}`,
              }}
            >
              Bn {upcoming.battalion.number}
            </span>
          )}
        </div>

        <div className="flex flex-col items-center gap-5">{punchControls}</div>
      </div>

      {/* Confirm Dialog */}
      {showConfirm && category && (
        <ConfirmDialog
          category={category}
          subcategory={subcategory}
          position={showOperationalFields ? position : null}
          apparatus={showOperationalFields ? apparatus : null}
          submitting={shiftSubmitting}
          onConfirm={handleConfirm}
          onCancel={() => setShowConfirm(false)}
          shiftTimeLabel={shiftTimeLabel}
        />
      )}

      <WelcomeNudge />

      {/* Retroactive shift detection overlay */}
      {retroCheck.retroactive && retroCheck.suggestionId && (
        <RetroactiveOverlay
          data={retroCheck.retroactive}
          suggestionId={retroCheck.suggestionId}
          onAccept={async (id, extra, clockTime) => {
            const success = await retroCheck.accept(id, extra, clockTime);
            if (success) window.location.reload();
            return success;
          }}
          onDismiss={retroCheck.dismiss}
        />
      )}
    </AppLayout>
  );
}

// -- Sub-components --

function ErrorBanner({ message, onDismiss }: { message: string; onDismiss: () => void }) {
  return (
    <div className="w-full px-4 py-3 bg-danger-500/15 border border-danger-500/25 rounded-xl flex justify-between items-center">
      <span className="text-sm text-danger-400">{message}</span>
      <button
        type="button"
        onClick={onDismiss}
        className="bg-transparent border-none text-danger-400 text-lg cursor-pointer px-1"
        aria-label="Dismiss error"
      >
        x
      </button>
    </div>
  );
}

function UpcomingShiftsSection({
  shifts,
  battalion,
  loading,
}: {
  shifts: UpcomingShift[];
  battalion: { id: number; name: string; number: number } | null;
  loading: boolean;
}) {
  if (loading) {
    return (
      <div className="w-full p-3 bg-surface-raised rounded-xl border border-edge-muted text-center text-sm text-content-tertiary">
        Loading schedule...
      </div>
    );
  }

  // No battalion assigned
  if (!battalion) {
    return (
      <div className="w-full p-3 bg-surface-raised rounded-xl border border-edge-muted text-center text-sm text-content-tertiary">
        No battalion assigned. Contact an officer to get your roster assignment.
      </div>
    );
  }

  // No upcoming shifts
  if (shifts.length === 0) {
    return (
      <div className="w-full p-3 bg-surface-raised rounded-xl border border-edge-muted text-center text-sm text-content-tertiary">
        No upcoming shifts found. Schedule may need to be regenerated.
      </div>
    );
  }

  const bnStyle = getBnStyle(battalion.number);
  const todayStr = new Date().toLocaleDateString('en-CA', { timeZone: 'America/New_York' });

  return (
    <div className="w-full flex flex-col gap-2">
      <div className="text-[11px] font-semibold text-content-tertiary uppercase tracking-wider">
        Your Upcoming Shifts
      </div>
      {shifts.map((shift) => {
        const isToday = shift.date === todayStr;
        const shiftDate = new Date(shift.shift_start);
        const dayLabel = shiftDate.toLocaleDateString('en-US', {
          weekday: 'short',
          month: 'short',
          day: 'numeric',
          timeZone: 'America/New_York',
        });
        const startTime = shiftDate.toLocaleTimeString('en-US', {
          hour: 'numeric',
          minute: '2-digit',
          hour12: true,
          timeZone: 'America/New_York',
        });
        const endTime = new Date(shift.shift_end).toLocaleTimeString('en-US', {
          hour: 'numeric',
          minute: '2-digit',
          hour12: true,
          timeZone: 'America/New_York',
        });

        return (
          <div
            key={shift.id}
            className="w-full p-3 rounded-xl flex items-center justify-between gap-3"
            style={{
              backgroundColor: isToday ? bnStyle.bg : 'var(--color-surface-raised)',
              border: isToday ? `2px solid ${bnStyle.border}` : '1px solid var(--color-edge-muted)',
            }}
          >
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <span
                  className={`text-sm font-semibold ${isToday ? '' : 'text-content-primary'}`}
                  style={isToday ? { color: bnStyle.text } : undefined}
                >
                  {dayLabel}
                </span>
                {isToday && (
                  <span
                    className="text-[10px] font-bold px-1.5 py-0.5 rounded uppercase tracking-wider"
                    style={{ backgroundColor: bnStyle.text, color: '#000' }}
                  >
                    Today
                  </span>
                )}
                {shift.is_weekend && (
                  <span className="text-[10px] font-semibold text-content-tertiary bg-surface-sunken px-1.5 py-0.5 rounded uppercase">
                    Weekend
                  </span>
                )}
                {shift.is_holiday && (
                  <span className="text-[10px] font-semibold text-accent-400 bg-accent-400/15 px-1.5 py-0.5 rounded">
                    Holiday
                  </span>
                )}
              </div>
              <div className="text-xs text-content-secondary mt-0.5">
                {startTime} – {endTime}
              </div>
            </div>
            <CoverageRequestButton
              dutyScheduleId={shift.id}
              shiftDate={dayLabel}
              battalionName={battalion.name}
              compact
            />
          </div>
        );
      })}
    </div>
  );
}

function SubmitModeToggle({
  mode,
  onModeChange,
  members,
  membersLoading,
  selectedMemberId,
  onMemberChange,
}: {
  mode: 'self' | 'other';
  onModeChange: (m: 'self' | 'other') => void;
  members: { id: number; display_name: string; email: string; battalion_id: number | null }[];
  membersLoading: boolean;
  selectedMemberId: number | null;
  onMemberChange: (id: number | null) => void;
}) {
  return (
    <div className="w-full">
      <div className="flex rounded-lg overflow-hidden border border-edge-default">
        <button
          type="button"
          onClick={() => onModeChange('self')}
          className={`flex-1 py-2.5 text-sm font-semibold transition-colors duration-150 cursor-pointer border-none ${
            mode === 'self'
              ? 'bg-action-primary text-content-on-primary'
              : 'bg-surface-raised text-content-secondary hover:bg-surface-sunken'
          }`}
        >
          For Me
        </button>
        <button
          type="button"
          onClick={() => onModeChange('other')}
          className={`flex-1 py-2.5 text-sm font-semibold transition-colors duration-150 cursor-pointer border-none border-l border-edge-default ${
            mode === 'other'
              ? 'bg-action-primary text-content-on-primary'
              : 'bg-surface-raised text-content-secondary hover:bg-surface-sunken'
          }`}
        >
          For Someone Else
        </button>
      </div>
      {mode === 'other' && (
        <div className="mt-3">
          <MemberSelector
            members={members}
            selected={selectedMemberId}
            onChange={onMemberChange}
            loading={membersLoading}
          />
        </div>
      )}
    </div>
  );
}
